import React, { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import { BASE_URL } from "../api";
import { getApiHeaders, getFullMediaUrl } from "../utils/helpers";
import Loader from "../component/Loader";
import MessageToster from "../component/MessageToster";
import { useHandleError } from "../hooks/useHandleError";
import { BRAND } from "../utils/brand";

const NEWS_FALLBACK_SVG = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 220" width="100%" height="100%">
  <defs>
    <linearGradient id="bgGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#eff6ff" />
      <stop offset="50%" stop-color="#f1f5f9" />
      <stop offset="100%" stop-color="#e2e8f0" />
    </linearGradient>
    <linearGradient id="symGrad" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#3b82f6" />
      <stop offset="100%" stop-color="#1d4ed8" />
    </linearGradient>
    <filter id="symShadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="4" stdDeviation="6" flood-color="#1e293b" flood-opacity="0.08" />
    </filter>
  </defs>
  <rect width="400" height="220" fill="url(#bgGrad)" />
  <g filter="url(#symShadow)">
    <circle cx="200" cy="95" r="42" fill="#ffffff" />
  </g>
  <g transform="translate(178, 73)">
    <rect x="3" y="4" width="38" height="34" rx="5" fill="none" stroke="url(#symGrad)" stroke-width="2.5" />
    <path d="M10 13h14M10 20h14M10 27h8" stroke="url(#symGrad)" stroke-width="2.2" stroke-linecap="round" />
    <rect x="27" y="12" width="11" height="11" rx="2.5" fill="url(#symGrad)" opacity="0.85" />
    <path d="M27 28h11" stroke="url(#symGrad)" stroke-width="2.2" stroke-linecap="round" />
  </g>
  <text x="200" y="165" text-anchor="middle" font-family="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" font-size="12" font-weight="700" fill="${BRAND.muted}" letter-spacing="1">MARKET NEWS</text>
</svg>
`)}`;

const formatNewsDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const cleanDateStr = String(dateStr).replace(" ", "T");
    const d = new Date(cleanDateStr);
    if (isNaN(d.getTime())) return String(dateStr);
    return d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dateStr);
  }
};

export default function News() {
  const handleError = useHandleError();
  const location = useLocation();
  const navigate = useNavigate();

  const [newsList, setNewsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(8);

  const [selectedNewsId, setSelectedNewsId] = useState(null);
  const [newsDetail, setNewsDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const fetchNewsList = async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const res = await axios.get(`${BASE_URL}/news/get_news_list`, {
        headers: getApiHeaders(),
        timeout: 10000,
      });

      if (res.data?.code === 200 || res.data?.status === true || res.data?.status === "success" || res.status === 200) {
        const rawData = res.data?.data || res.data?.news || res.data?.news_list || res.data || [];
        const actualList = Array.isArray(rawData)
          ? rawData.flat()
          : Array.isArray(rawData?.articles)
            ? rawData.articles
            : [];
        setNewsList(actualList);
        if (isRefresh) {
          MessageToster("success", res.data?.message || "News list refreshed successfully!");
        }
      } else {
        setNewsList([]);
      }
    } catch (err) {
      console.log("get_news_list error:", err?.message);
      handleError(err);
      setNewsList([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchNewsById = async (newsId) => {
    if (!newsId) return;
    setDetailLoading(true);
    setModalOpen(true);
    setSelectedNewsId(newsId);

    const cached = newsList.find((n) => String(n.id) === String(newsId));
    if (cached) {
      setNewsDetail(cached);
    } else {
      setNewsDetail(null);
    }

    try {
      const res = await axios.get(`${BASE_URL}/news/get_news_by_id?id=${newsId}`, {
        headers: getApiHeaders(),
        timeout: 10000,
      });

      if (res.data?.code === 200 || res.data?.status === true || res.data?.status === "success" || res.status === 200) {
        const data = res.data?.data || res.data?.news || res.data;
        setNewsDetail(data);
      } else {
        MessageToster("error", res.data?.message || "Failed to load news details");
      }
    } catch (err) {
      console.log("get_news_by_id error:", err?.message);
      handleError(err);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setNewsDetail(null);
    setSelectedNewsId(null);
    if (location.search.includes("id=")) {
      navigate("/news", { replace: true });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeModal();
      }
    };
    if (modalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen]);

  useEffect(() => {
    fetchNewsList();

    const queryParams = new URLSearchParams(location.search);
    const idFromUrl = queryParams.get("id");
    if (idFromUrl) {
      fetchNewsById(idFromUrl);
    }
  }, []);

  const filteredNews = useMemo(() => {
    return newsList.filter((item) => {
      if (item.is_active === false || item.is_active === 0 || item.is_active === "false") return false;

      const isExclusive = Boolean(item.is_exclusive === true || item.is_exclusive === 1 || item.is_exclusive === "true");
      if (activeFilter === "exclusive" && !isExclusive) return false;
      if (activeFilter === "general" && isExclusive) return false;

      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const titleMatch = (item.title || item.headline || "").toLowerCase().includes(q);
        const descMatch = (item.description_preview || item.description || "").toLowerCase().includes(q);
        return titleMatch || descMatch;
      }

      return true;
    });
  }, [newsList, activeFilter, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(filteredNews.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, filteredNews.length);
  const paginatedNews = useMemo(() => {
    return filteredNews.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredNews, currentPage, itemsPerPage]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const resolveImageUrl = (img) => {
    if (!img || typeof img !== "string" || img.trim() === "") {
      return NEWS_FALLBACK_SVG;
    }
    return getFullMediaUrl(img) || NEWS_FALLBACK_SVG;
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}>
          <a
            className="page-link"
            href="#!"
            onClick={(e) => {
              e.preventDefault();
              handlePageChange(i);
            }}
          >
            {i}
          </a>
        </li>
      );
    }
    return pages;
  };

  return (
    <>
      <div id="layout-wrapper">
        {loading && <Loader />}
        <Topbar />
        <Sidebar />

        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* Page Title & Actions */}
              <div className="row mb-3">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between p-0 mb-2 bg-transparent">
                    <div className="d-flex align-items-center gap-2">
                      <h2 className="fw-700 mb-0 font-size-22">Latest News</h2>
                    </div>

                    <div className="page-title-right mt-3 mt-sm-0 d-flex flex-wrap align-items-center justify-content-sm-end gap-3">
                      {/* Tabs */}
                      <ul className="nav nav-pills bg-white p-2 rounded-pill" role="tablist" style={{ border: "1px solid var(--color-border)" }}>
                        <li className="nav-item">
                          <a
                            className={`nav-link rounded-pill px-3 py-1 ${activeFilter === "all" ? "active" : ""}`}
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveFilter("all");
                              setCurrentPage(1);
                            }}
                          >
                            All News
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className={`nav-link rounded-pill px-3 py-1 ${activeFilter === "exclusive" ? "active" : ""}`}
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveFilter("exclusive");
                              setCurrentPage(1);
                            }}
                          >
                            <i className="bx bxs-star text-warning me-1" />
                            Exclusive
                          </a>
                        </li>
                        <li className="nav-item">
                          <a
                            className={`nav-link rounded-pill px-3 py-1 ${activeFilter === "general" ? "active" : ""}`}
                            href="#!"
                            onClick={(e) => {
                              e.preventDefault();
                              setActiveFilter("general");
                              setCurrentPage(1);
                            }}
                          >
                            General
                          </a>
                        </li>
                      </ul>

                      {/* Search Bar */}
                      <div className="search-box flex-grow-1 flex-sm-grow-0" style={{ minWidth: "240px", maxWidth: "340px" }}>
                        <div className="position-relative">
                          <input
                            type="text"
                            className="form-control form-control-sm rounded py-2"
                            placeholder="Search news..."
                            value={searchQuery}
                            onChange={(e) => {
                              setSearchQuery(e.target.value);
                              setCurrentPage(1);
                            }}
                            style={{ paddingLeft: "35px", height: "40px", border: "1px solid var(--color-border)" }}
                          />
                          <i className="bx bx-search search-icon position-absolute text-muted font-size-14" style={{ left: "12px", top: "50%", transform: "translateY(-50%)" }}></i>
                          {searchQuery && (
                            <i
                              className="bx bx-x position-absolute text-muted font-size-16"
                              style={{ right: "12px", top: "50%", transform: "translateY(-50%)", cursor: "pointer" }}
                              onClick={() => setSearchQuery("")}
                              title="Clear Search"
                            ></i>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* News Grid (Compact 4-column cards) */}
              {paginatedNews.length > 0 ? (
                <div className="row g-3">
                  {paginatedNews.map((item, index) => {
                    const isExclusive = Boolean(item.is_exclusive === true || item.is_exclusive === 1 || item.is_exclusive === "true");
                    const dateStr = item.ondate || item.created_at || item.published_at || "";
                    const imgUrl = resolveImageUrl(item.image);
                    const descText = item.description_preview || item.description || "";

                    return (
                      <div className="col-xxl-3 col-xl-3 col-lg-4 col-md-6 col-12 mb-2" key={item.id || index}>
                        <div
                          className="card h-100 shadow-sm news-card overflow-hidden d-flex flex-column justify-content-between"
                          style={{
                            borderRadius: "10px",
                            transition: "all 0.2s ease-in-out",
                            cursor: "pointer",
                            border: "1px solid var(--color-border)"
                          }}
                          onClick={() => fetchNewsById(item.id)}
                        >
                          <div>
                            {/* Card Top: 200px height with actual image or symbol fallback */}
                            <div
                              className="position-relative"
                              style={{ height: "200px", overflow: "hidden", backgroundColor: "#f1f5f9" }}
                            >
                              <img
                                src={imgUrl}
                                alt={item.title || "News"}
                                className="w-100 h-100"
                                style={{ objectFit: "cover", transition: "transform 0.25s ease" }}
                                onError={(e) => {
                                  if (e.currentTarget.src !== NEWS_FALLBACK_SVG) {
                                    e.currentTarget.src = NEWS_FALLBACK_SVG;
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.transform = "scale(1.04)";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.transform = "scale(1)";
                                }}
                              />

                              {/* Exclusive Badge on Image */}
                              {isExclusive && (
                                <span
                                  className="badge position-absolute top-0 start-0 m-2 px-2 py-1 font-size-10 shadow"
                                  style={{
                                    background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                                    color: "#fff",
                                    borderRadius: "12px",
                                  }}
                                >
                                  <i className="bx bxs-star me-1" /> Exclusive
                                </span>
                              )}
                            </div>

                            {/* Card Content */}
                            <div className="card-body p-3 pb-1">
                              <h6
                                className="card-title text-dark fw-bold mb-1 text-truncate-2"
                                style={{
                                  fontSize: "14px",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: "1.3",
                                  minHeight: "2.6em",
                                }}
                              >
                                {item.title || "Untitled News"}
                              </h6>

                              <p
                                className="text-muted font-size-12 mb-2"
                                style={{
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                  lineHeight: "1.4",
                                  minHeight: "2.8em",
                                }}
                              >
                                {descText
                                  ? descText.replace(/<[^>]*>?/gm, "")
                                  : "Click below to read full details of this announcement."}
                              </p>
                            </div>
                          </div>

                          {/* Card Footer */}
                          <div className="card-footer bg-transparent border-top p-2 px-3 d-flex align-items-center justify-content-between">
                            <span className="text-muted font-size-11 d-flex align-items-center gap-1">
                              {dateStr && (
                                <>
                                  <i className="bx bx-calendar" /> {formatNewsDate(dateStr)}
                                </>
                              )}
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-soft-primary d-inline-flex align-items-center gap-1 rounded-pill px-3 py-1 font-size-11"
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchNewsById(item.id);
                              }}
                            >
                              Read Article <i className="bx bx-right-arrow-alt font-size-14" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : !loading ? (
                /* Empty state */
                <div className="row">
                  <div className="col-12">
                    <div className="card border-0 shadow-sm text-center py-5">
                      <div className="card-body">
                        <div className="avatar-lg mx-auto mb-3">
                          <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-36">
                            <i className="bx bx-news" />
                          </span>
                        </div>
                        <h4 className="fw-semibold font-size-16">No News Articles Found</h4>
                        <p className="text-muted mb-3 max-w-md mx-auto font-size-13" style={{ maxWidth: "450px" }}>
                          {searchQuery
                            ? `No results matched "${searchQuery}". Try searching with different keywords.`
                            : "There are currently no news updates published in this section."}
                        </p>
                        {searchQuery ? (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary px-3"
                            onClick={() => setSearchQuery("")}
                          >
                            Clear Search
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary px-3"
                            onClick={() => fetchNewsList(true)}
                          >
                            <i className="bx bx-refresh me-1" /> Refresh News
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Pagination & Info Bar */}
              {filteredNews.length > 0 && (
                <div className="row mt-3 mb-4 align-items-center">
                  <div className="col-sm-12 col-md-6 d-flex flex-column flex-sm-row align-items-center justify-content-center justify-content-md-start">
                    <div className="dataTables_length me-sm-3 mb-2 mb-sm-0">
                      <label className="d-flex align-items-center mb-0">
                        Show
                        <select
                          className="form-select form-select-sm d-inline-block w-auto mx-2"
                          value={itemsPerPage}
                          onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                        >
                          {[8, 16, 24, 32, 40].map((size) => (
                            <option key={size} value={size}>
                              {size}
                            </option>
                          ))}
                        </select>
                        entries
                      </label>
                    </div>
                    <div className="dataTables_info text-muted d-none d-sm-block font-size-12">
                      Showing {startIndex + 1} to {Math.min(endIndex, filteredNews.length)} of {filteredNews.length} entries
                    </div>
                  </div>

                  <div className="col-sm-12 col-md-6 d-flex justify-content-center justify-content-md-end">
                    <ul className="pagination pagination-sm mb-0">
                      <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                        <a
                          className="page-link"
                          href="#!"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage > 1) handlePageChange(currentPage - 1);
                          }}
                        >
                          Previous
                        </a>
                      </li>
                      {renderPageNumbers()}
                      <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                        <a
                          className="page-link"
                          href="#!"
                          onClick={(e) => {
                            e.preventDefault();
                            if (currentPage < totalPages) handlePageChange(currentPage + 1);
                          }}
                        >
                          Next
                        </a>
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
          </div>

          <Footer />
        </div>
      </div>

      {/* 3. News Detail Modal (/news/get_news_by_id) */}
      {modalOpen && (
        <div
          className="modal fade show d-block"
          tabIndex="-1"
          role="dialog"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.65)",
            backdropFilter: "blur(5px)",
            zIndex: 1055,
          }}
          onClick={closeModal}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content" style={{ borderRadius: "16px", overflow: "hidden" }}>
              {/* Modal Header */}
              <div className="modal-header d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <span className="avatar-xs">
                    <span className="avatar-title rounded-circle bg-primary-subtle text-primary font-size-16">
                      <i className="bx bx-news" />
                    </span>
                  </span>
                  <h5 className="modal-title mb-0">Article Details</h5>
                  {Boolean(newsDetail?.is_exclusive === true || newsDetail?.is_exclusive === 1 || newsDetail?.is_exclusive === "true") && (
                    <span className="badge bg-warning text-dark font-size-11 px-2 py-1">
                      <i className="bx bxs-star me-1" /> Exclusive
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeModal}
                />
              </div>

              {/* Modal Body */}
              <div className="modal-body p-4" style={{ maxHeight: "75vh", overflowY: "auto" }}>
                {detailLoading && !newsDetail ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary mb-3" role="status" />
                    <h6 className="text-muted">Loading article details...</h6>
                  </div>
                ) : newsDetail ? (
                  <div>
                    {/* Article Cover Image with fallback */}
                    <div
                      className="mb-4 rounded-3 overflow-hidden shadow-sm text-center"
                      style={{ maxHeight: "320px", backgroundColor: "#f8f9fa" }}
                    >
                      <img
                        src={resolveImageUrl(newsDetail.image)}
                        alt={newsDetail.title || "News Cover"}
                        className="img-fluid w-100"
                        style={{ maxHeight: "320px", objectFit: "cover" }}
                        onError={(e) => {
                          if (e.currentTarget.src !== NEWS_FALLBACK_SVG) {
                            e.currentTarget.src = NEWS_FALLBACK_SVG;
                          }
                        }}
                      />
                    </div>

                    {/* Meta Bar */}
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 text-muted font-size-13 mb-3 pb-2 border-bottom">
                      <div className="d-flex align-items-center gap-3">
                        {(newsDetail.ondate || newsDetail.created_at || newsDetail.published_at) && (
                          <span>
                            <i className="bx bx-calendar me-1 text-primary" />
                            {formatNewsDate(newsDetail.ondate || newsDetail.created_at || newsDetail.published_at)}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Headline / Title */}
                    <h4 className="fw-bold mb-3 text-dark" style={{ lineHeight: "1.3" }}>
                      {newsDetail.title || "Untitled Article"}
                    </h4>

                    {/* Content / Description */}
                    <div
                      className="text-secondary font-size-14"
                      style={{ lineHeight: "1.8", whiteSpace: "pre-line" }}
                    >
                      {newsDetail.description || newsDetail.description_preview ? (
                        /<[a-z][\s\S]*>/i.test(newsDetail.description || newsDetail.description_preview) ? (
                          <div dangerouslySetInnerHTML={{ __html: newsDetail.description || newsDetail.description_preview }} />
                        ) : (
                          newsDetail.description || newsDetail.description_preview
                        )
                      ) : (
                        <p className="text-muted fst-italic">No additional description available for this article.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <p className="text-muted">Article details could not be loaded.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
